import { supportTicketApi } from '@/api/queries/supportTicket'
import Button from '@/components/UI/New/Button'
import { Modal } from 'antd'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { PlatformSupportContext } from './PlatformSupportLauncher'
import classes from './announcement.module.css'

const SupportArtwork = () => (
  <div className={classes.artwork} aria-hidden="true">
    <span className={classes.orbit} />
    <span className={classes.orbitInner} />
    <svg className={classes.headset} viewBox="0 0 120 120" fill="none">
      <rect x="15" y="15" width="90" height="90" rx="30" fill="currentColor" opacity=".13" />
      <path d="M34 61v-9a26 26 0 0 1 52 0v9" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <rect x="27" y="54" width="15" height="26" rx="7" fill="currentColor" />
      <rect x="78" y="54" width="15" height="26" rx="7" fill="currentColor" />
      <path d="M85 78v4a12 12 0 0 1-12 12H62" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <rect x="53" y="89" width="15" height="10" rx="5" fill="currentColor" />
    </svg>
    <span className={classes.chatBubble}><i /><i /><i /></span>
    <span className={classes.sparkle}>✦</span>
  </div>
)

export const SupportAnnouncementBanner = () => {
  const support = useContext(PlatformSupportContext)
  if (!support?.announcementEnabled) return null
  const open = () => {
    if (support.openSupport) support.openSupport()
    else if (!support.open) support.toggle()
  }
  return (
    <section className={classes.banner} aria-label="Техподдержка платформы">
      <div className={classes.bannerCopy}>
        <span className={classes.kicker}><span />Новое в платформе</span>
        <h3>Техподдержка уже рядом</h3>
        <p>Вопросы по платформе? Поможем с настройками и разберёмся вместе.</p>
        <Button className={classes.bannerButton} onClick={open}>Написать в поддержку <span aria-hidden="true">↗</span></Button>
      </div>
      <SupportArtwork />
    </section>
  )
}

interface Props {
  profileId: number | string
  enabled: boolean
  pending: boolean
  blocked: boolean
  onClaimed: () => void
  onOpenSupport: () => void
}

const SupportAnnouncement: FC<Props> = ({ profileId, enabled, pending, blocked, onClaimed, onOpenSupport }) => {
  const [visible, setVisible] = useState(false)
  const mounted = useRef(false)
  const identity = useRef(profileId)
  const enabledRef = useRef(enabled)
  const inFlight = useRef<number | string | null>(null)
  const claimed = useRef<number | string | null>(null)
  const lastAttempt = useRef(0)
  identity.current = profileId
  enabledRef.current = enabled

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  useEffect(() => { setVisible(false); lastAttempt.current = 0 }, [profileId])

  useEffect(() => {
    if (!enabled || !pending || blocked) return undefined
    const check = () => {
      if (document.hidden || document.documentElement.classList.contains('google-translation-pending')) return
      if (Array.from(document.querySelectorAll('[role="dialog"]')).some((dialog) => dialog.getClientRects().length > 0)) return
      if (inFlight.current !== null || claimed.current === profileId || Date.now() - lastAttempt.current < 10000) return
      inFlight.current = profileId
      lastAttempt.current = Date.now()
      void supportTicketApi.claimAnnouncement().then((response) => {
        if (!mounted.current || identity.current !== profileId || !enabledRef.current) return
        if (response.data?.status !== 'success') return
        claimed.current = profileId
        setVisible(Boolean(response.data.data.show))
        onClaimed()
      }).catch(() => {
        // A transient error leaves the receipt pending; the next attempt can claim it.
      }).finally(() => { inFlight.current = null })
    }
    check()
    const interval = window.setInterval(check, 1000)
    return () => window.clearInterval(interval)
  }, [enabled, pending, blocked, profileId, onClaimed])

  return (
    <Modal
      open={enabled && visible && !blocked}
      title="Знакомьтесь, техподдержка Impulse"
      onCancel={() => setVisible(false)}
      footer={null}
      centered
      width={600}
      className={classes.modal}
      destroyOnClose
    >
      <div className={classes.modalHero}><SupportArtwork /><span>Помощь с платформой — в одном окне</span></div>
      <p className={classes.intro}>Теперь связаться с командой Impulse можно прямо из личного кабинета. Мы поможем найти ответ и разобраться в вопросах о платформе.</p>
      <ul className={classes.features}>
        <li><span aria-hidden="true">01</span><div><strong>Выберите тему</strong><p>Настройки, оплата или доработки — направим вопрос по адресу.</p></div></li>
        <li><span aria-hidden="true">02</span><div><strong>Расскажите о задаче</strong><p>Напишите сообщение и приложите скриншот, если он поможет.</p></div></li>
        <li><span aria-hidden="true">03</span><div><strong>Продолжайте работу</strong><p>Переписка и ответы сохранятся в разделе «Техподдержка».</p></div></li>
      </ul>
      <div className={classes.modalActions}>
        <Button onClick={() => { setVisible(false); onOpenSupport() }}>Открыть техподдержку <span aria-hidden="true">↗</span></Button>
        <Button color="gray" onClick={() => setVisible(false)}>Посмотрю позже</Button>
      </div>
      <p className={classes.hint}>Кнопка «Техподдержка» всегда под рукой — в меню платформы.</p>
    </Modal>
  )
}

export default SupportAnnouncement
